import { Source } from "@opendash/plugin-parse/dist/types-generated";
import Parse from "parse";
import {
  Item,
  Media,
  Schedule_Execution,
  Schedule_Execution_Step,
  Schedule_Step,
  Schedule_Template,
} from "../../../js/parsegenerated";
import { Material } from "../../../types/Material";

export type SaveExecutionValues = {
  sources: Source[];
  title: string;
  description: string;
  steps: string[];
  items: MaterialObject;
  files: Parse.File[];
  date?: Date;
  schedule?: Schedule_Template;
};

export type MaterialObject = {
  [index: string]: Array<{ selected: Material }>;
};

export type Step = {
  description: string;
  id: string;
};

export const useSaveExecution = () => {
  /**
   * Saves an existing execution, updating the execution object with the new values
   * @param values form values
   * @param execution the execution object to update
   * @returns the updated execution object
   */
  async function saveExistingExecution(
    values: SaveExecutionValues,
    execution: Schedule_Execution
  ) {
    const allSelectedItems = await fetchAffectedItems(values.items);
    const savedMedia = await saveMedia(values.files);
    const stepExeObjects = await new Parse.Query(Schedule_Execution_Step)
      .equalTo("scheduleexecution", execution)
      .find();
    const stepObjects = await new Parse.Query(Schedule_Step)
      .containedIn("objectId", values.steps)
      .find();

    const stepsToSave = [];

    for (const stepId of values.steps) {
      // one of those is undefined, the other should be the step object
      const step = stepObjects.find((s) => s.id === stepId);
      const stepExe = stepExeObjects.find((s) => s.id === stepId);

      if (step) {
        const stepToSave = new Schedule_Execution_Step({
          scheduleexecution: execution,
          comment: "",
          done: true,
          title: step.get("title"),
          description: step.get("description"),
        });
        stepsToSave.push(stepToSave);
      }

      if (stepExe) {
        const stepToSave = new Schedule_Execution_Step({
          scheduleexecution: execution,
          comment: stepExe.get("comment"),
          done: stepExe.get("done"),
          title: stepExe.get("title"),
          description: stepExe.get("description"),
        });
        stepsToSave.push(stepToSave);
      }
    }
    const savedSteps = await Parse.Object.saveAll(stepsToSave);
    const destroyed = await Parse.Object.destroyAll(stepExeObjects);

    execution.set("title", values.title);
    execution.set("description", values.description);
    if (values.date) execution.set("finishedAt", values.date);
    execution.set(
      "material",
      values.items[values.sources[0].id]?.map((i) => i.selected)
    );

    const media = await execution.get("media").query().find();
    media.forEach((m) => {
      execution.get("media").remove(m);
      m.destroy();
    });
    execution.get("media").add(savedMedia);

    const saved = await execution.save();
    return saved;
  }

  async function saveNewExecution(values: SaveExecutionValues) {
    const allSelectedItems = await fetchAffectedItems(values.items);
    const savedMedia = await saveMedia(values.files);

    const sourceObjects = values.sources;

    const stepObjects = await new Parse.Query(Schedule_Step)
      .containedIn("objectId", values.steps)
      .find();

    const executions = sourceObjects.map((source) => {
      return new Schedule_Execution({
        title: values.title,
        description: values.description,
        source: source,
        finishedAt: values.date ?? new Date(),
        origin: values.schedule,
        origin_cron: values.schedule?.get("cron"),
      });
    });
    const saved = await Schedule_Execution.saveAll(executions);

    saved.forEach((exe) => {
      const exe_steps = stepObjects.map((step) => {
        return new Schedule_Execution_Step({
          scheduleexecution: exe,
          comment: "",
          done: true,
          title: step.get("title"),
          description: step.get("description"),
        });
      });
      Schedule_Execution_Step.saveAll(exe_steps);

      if (values.items[exe.source.id] !== undefined) {
        const materials = values.items[exe.source.id].map((item) => {
          const selectedItem = allSelectedItems.find(
            (i) => i.id === item.selected?.id
          );

          selectedItem?.set(
            "stock",
            (selectedItem.get("stock") ?? 0) - item.selected?.quantity
          );
          return item.selected;
        });
        exe.set("material", materials);
      }

      if (savedMedia.length > 0) {
        exe.set("media", new Parse.Relation());
        exe.get("media").add(savedMedia);
      }
    });
    await Item.saveAll(allSelectedItems);

    return saved;
  }

  return { saveExistingExecution, saveNewExecution };
};

async function fetchAffectedItems(items: MaterialObject) {
  const itemIds = Object.keys(items).reduce((acc, key) => {
    return acc.concat(
      items[key]?.map((item) => item.selected?.id?.toString()) ?? []
    );
  }, [] as string[]);

  const allSelectedItems = await new Parse.Query(Item)
    .containedIn("objectId", itemIds)
    .find();

  return allSelectedItems;
}

async function saveMedia(files: Parse.File[]) {
  const savedMedia = await Parse.Object.saveAll(
    files.map((file) => new Media({ media: file }))
  );

  return savedMedia;
}
